/* Bastant site 2026 — product page behaviour (sections bs-product-*):
   gallery, finish swatches across sibling products, variant options, stock line, sold-out "Giv mig besked"
   (Klaviyo back-in-stock), add to cart through Impact's cart drawer, sticky mobile bar, "Byg sættet færdigt",
   and related products loaded from Shopify recommendations. */
(() => {
  if (window.BastantPDP) return;

  const SCROLL_KEY = 'bsPdpScroll';
  const NUMBER_WORDS = ['nul', 'én', 'to', 'tre', 'fire', 'fem', 'seks', 'syv', 'otte', 'ni', 'ti'];

  const formatKr = (cents) => {
    const value = Math.round(Number(cents) || 0);
    const kr = Math.floor(value / 100);
    const ore = value % 100;
    const whole = String(kr).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${whole}${ore ? ',' + String(ore).padStart(2, '0') : ''} kr.`;
  };

  const fill = (template, tokens) =>
    String(template || '').replace(/\[([a-z_]+)\]/g, (match, key) => (key in tokens ? tokens[key] : match));

  const routes = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const readJSON = (el) => {
    try {
      return JSON.parse((el && el.textContent) || '{}');
    } catch (e) {
      return {};
    }
  };

  const ADD_ERROR = 'Varen kunne ikke lægges i kurven. Prøv igen om et øjeblik.';
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const safeJSON = async (response) => {
    try {
      return await response.json();
    } catch (e) {
      return null;
    }
  };

  /* fetch that waits and tries again when Shopify answers 429 (too many requests); nothing was processed then. */
  async function fetchRetry(url, options, tries = 3) {
    let response;
    for (let i = 0; i < tries; i += 1) {
      response = await fetch(url, options);
      if (response.status !== 429) return response;
      await sleep(800 * (i + 1));
    }
    return response;
  }

  /* Same flow as the live LP (assets/bastant-lp.js): ask Impact for its bundled sections, add through the
     Ajax API, then fire variant:add so Impact opens its cart drawer and updates the header count. */
  async function addToCart(items, source) {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );

    const response = await fetchRetry(`${routes()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sections.join(',') }),
    });
    const json = await safeJSON(response);

    if (!response.ok || !json) {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      throw new Error((json && (json.description || json.message)) || ADD_ERROR);
    }

    const settings = (window.themeVariables && window.themeVariables.settings) || {};
    if (settings.cartType === 'page') {
      window.location.href = `${routes()}cart`;
      return;
    }

    const cartResponse = await fetchRetry(`${routes()}cart.js`, { headers: { Accept: 'application/json' } });
    const cart = cartResponse.ok ? await safeJSON(cartResponse) : null;
    if (!cart) {
      /* The items are in the cart; let Impact fetch the cart itself and open its drawer. */
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      if (settings.cartType === 'drawer') document.getElementById('cart-drawer')?.show?.();
      return;
    }
    cart.sections = json.sections;

    (source || document.documentElement).dispatchEvent(
      new CustomEvent('variant:add', { bubbles: true, detail: { items: json.items || [json], cart } })
    );
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } })
    );
  }

  function setBusy(button, busy) {
    if (!button) return;
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
    button.disabled = busy;
  }

  /* ---------- Gallery ---------- */

  function bindGallery(gallery) {
    if (gallery.dataset.bsBound) return;
    gallery.dataset.bsBound = 'true';
    const main = gallery.querySelector('[data-bs-gallery-main]');
    const img = main && main.querySelector('img');
    const thumbs = Array.from(gallery.querySelectorAll('[data-bs-thumb]'));
    if (!img || thumbs.length < 2) return;

    let index = Math.max(0, thumbs.findIndex((t) => t.getAttribute('aria-current') === 'true'));

    const show = (i) => {
      index = (i + thumbs.length) % thumbs.length;
      const thumb = thumbs[index];
      img.srcset = thumb.dataset.srcset;
      img.src = thumb.dataset.src;
      img.alt = thumb.dataset.alt || '';
      thumbs.forEach((t) => t.setAttribute('aria-current', t === thumb ? 'true' : 'false'));
      const strip = thumb.parentElement;
      if (strip && strip.scrollWidth > strip.clientWidth) {
        strip.scrollTo({ left: thumb.offsetLeft - strip.clientWidth / 2 + thumb.clientWidth / 2, behavior: 'smooth' });
      }
    };

    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => show(i)));
    gallery.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') show(index + 1);
      if (event.key === 'ArrowLeft') show(index - 1);
    });

    /* Swipe on the big photo (mobile). */
    let startX = null;
    let startY = null;
    main.addEventListener('pointerdown', (e) => {
      startX = e.clientX;
      startY = e.clientY;
    });
    main.addEventListener('pointerup', (e) => {
      if (startX === null) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      startX = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) show(index + (dx < 0 ? 1 : -1));
    });
    main.addEventListener('pointercancel', () => {
      startX = null;
    });

    gallery.bsShowMedia = (mediaId) => {
      const i = thumbs.findIndex((t) => t.dataset.mediaId === String(mediaId));
      if (i > -1) show(i);
    };
  }

  /* ---------- Buy box ---------- */

  function bindProduct(root) {
    if (root.dataset.bsBound) return;
    root.dataset.bsBound = 'true';

    const data = readJSON(root.querySelector('[data-bs-pdp-data]'));
    const texts = data.texts || {};
    const variants = data.variants || [];
    const gallery = root.querySelector('[data-bs-gallery]');

    const el = {
      price: root.querySelector('[data-bs-price]'),
      compare: root.querySelector('[data-bs-compare]'),
      finishValue: root.querySelector('[data-bs-finish-value]'),
      stock: root.querySelector('[data-bs-stock]'),
      stockText: root.querySelector('[data-bs-stock-text]'),
      stockLeft: root.querySelector('[data-bs-stock-left]'),
      atc: root.querySelector('[data-bs-atc]'),
      atcLabel: root.querySelector('[data-bs-atc-label]'),
      preorderProp: root.querySelector('[data-bs-preorder-prop]'),
      variantInput: root.querySelector('[data-bs-variant-input]'),
      cartForm: root.querySelector('[data-bs-cart]'),
      cartError: root.querySelector('[data-bs-cart-error]'),
      notify: root.querySelector('[data-bs-notify]'),
      notifyTitle: root.querySelector('[data-bs-notify-title]'),
      notifyText: root.querySelector('[data-bs-notify-text]'),
      notifyForm: root.querySelector('[data-bs-notify-form]'),
      notifyDone: root.querySelector('[data-bs-notify-done]'),
      notifyError: root.querySelector('[data-bs-notify-error]'),
      actions: root.querySelector('[data-bs-actions]'),
      notifyWrap: root.querySelector('[data-bs-notify-wrap]'),
      bar: root.querySelector('[data-bs-bar]'),
      barPrice: root.querySelector('[data-bs-bar-price]'),
      barMeta: root.querySelector('[data-bs-bar-meta]'),
      barBtn: root.querySelector('[data-bs-bar-btn]'),
    };

    const swatches = Array.from(root.querySelectorAll('[data-bs-swatch]'));
    const altRows = Array.from(root.querySelectorAll('[data-bs-alt-row]'));
    const optionGroups = Array.from(root.querySelectorAll('[data-bs-option]'));

    let currentVariant =
      variants.find((v) => String(v.id) === String(el.variantInput && el.variantInput.value)) || variants[0];

    /* What the buy box currently shows: this product's variant, or a sold-out sibling picked in the finish row. */
    let view = null;

    const viewFromVariant = (v) => ({
      own: true,
      name: data.finishName || '',
      key: data.finishKey || '',
      price: v.price,
      compare: v.compare,
      available: v.available,
      stock: v.stock,
      qty: v.qty,
      ship: v.ship || '',
      batch: v.batch || 0,
      left: v.left == null ? null : Number(v.left),
      variantId: v.id,
      restock: data.restock || '',
    });

    const stockText = (state, qty, ship) => {
      if (state === 'preorder') return fill(texts.stock_preorder, { dato: ship });
      if (state === 'low') return fill(texts.stock_low, { antal: qty });
      if (state === 'production') return texts.stock_production || '';
      return texts.stock_in || '';
    };
    /* Preorder batch from the portal: "Næste hold: 250 stk. · 87 tilbage", only while fewer than preorder_left_below are left. */
    const leftText = (v) => {
      const below = Number(texts.preorder_left_below) || 100;
      if (!v.available || v.stock !== 'preorder' || !texts.stock_preorder_left || v.left == null) return '';
      return v.left > 0 && v.left < below ? fill(texts.stock_preorder_left, { hold: v.batch, tilbage: v.left }) : '';
    };
    const barText = (state, qty, ship) => {
      if (state === 'out') return texts.bar_out || '';
      if (state === 'preorder') return fill(texts.bar_preorder, { dato: ship });
      if (state === 'low') return fill(texts.bar_low, { antal: qty });
      if (state === 'production') return texts.bar_production || '';
      return texts.bar_in || '';
    };

    const render = () => {
      const v = view;
      const priceText = formatKr(v.price);
      if (el.price) el.price.textContent = priceText;
      if (el.compare) {
        const hasCompare = Number(v.compare) > Number(v.price);
        el.compare.hidden = !hasCompare;
        el.compare.textContent = hasCompare ? formatKr(v.compare) : '';
      }
      if (el.finishValue && v.name) el.finishValue.textContent = v.name;

      if (el.stock) {
        el.stock.className = `bs-pdp__stock bs-pdp__stock--${v.available ? v.stock : 'out'}`;
        if (el.stockText) el.stockText.textContent = v.available ? stockText(v.stock, v.qty, v.ship) : '';
      }
      if (el.stockLeft) {
        const left = leftText(v);
        el.stockLeft.textContent = left;
        el.stockLeft.hidden = !left;
      }

      const preorder = v.available && v.stock === 'preorder';
      if (el.atcLabel) el.atcLabel.textContent = fill(preorder && texts.preorder_button ? texts.preorder_button : texts.cart_button, { pris: priceText });
      if (el.preorderProp) {
        el.preorderProp.disabled = !preorder;
        el.preorderProp.value = preorder ? v.ship : '';
      }
      if (el.atc) el.atc.disabled = !v.available;
      if (el.variantInput && v.own) el.variantInput.value = v.variantId;

      root.dataset.mode = v.available ? 'cart' : 'notify';

      if (!v.available && el.notify) {
        const finish = (v.name || '').toLowerCase();
        if (el.notifyTitle) {
          el.notifyTitle.textContent = finish ? fill(texts.notify_title, { finish }) : texts.notify_title_plain || '';
        }
        if (el.notifyText) {
          el.notifyText.textContent = v.restock
            ? fill(texts.notify_text_date, { finish, tilbage: v.restock })
            : fill(texts.notify_text, { finish });
        }
        const variantField = el.notifyForm && el.notifyForm.querySelector('[name="variant"]');
        if (variantField && variantField.value !== String(v.variantId)) {
          variantField.value = v.variantId;
          el.notifyForm.hidden = false;
          if (el.notifyDone) el.notifyDone.hidden = true;
          if (el.notifyError) el.notifyError.textContent = '';
        }
      }

      /* Alternatives: everything in stock except the finish being shown. */
      altRows.forEach((row) => {
        row.hidden = v.own ? row.hasAttribute('data-current') : false;
      });

      if (el.barPrice) el.barPrice.textContent = priceText;
      if (el.barMeta) {
        const status = barText(v.available ? v.stock : 'out', v.qty, v.ship);
        el.barMeta.textContent = [v.name, status].filter(Boolean).join(' · ');
      }
      if (el.barBtn) el.barBtn.textContent = v.available ? texts.sticky_button : texts.sticky_notify_button;
      const barDot = root.querySelector('[data-bs-bar-dot]');
      if (barDot && v.key) {
        barDot.className = barDot.className.replace(/bs-swatch--[a-z]+/, `bs-swatch--${v.key}`);
      }
      updateBar();
    };

    /* ---- Finish swatches (sibling products) ---- */

    const checkSwatch = (swatch) => {
      swatches.forEach((s) => {
        const on = s === swatch;
        s.setAttribute('aria-checked', on ? 'true' : 'false');
        s.tabIndex = on ? 0 : -1;
      });
    };

    const currentSwatch = swatches.find((s) => s.dataset.current === 'true');

    swatches.forEach((swatch, i) => {
      swatch.addEventListener('click', (event) => {
        const isCurrent = swatch.dataset.current === 'true';
        if (!isCurrent) {
          /* A real link to the other finish, sold out or not (Gry 28/10: every finish has its own page,
             so it can be shared and wish-listed): keep the visitor where they are on the page. */
          try {
            sessionStorage.setItem(
              SCROLL_KEY,
              JSON.stringify({ path: new URL(swatch.href, location.href).pathname, y: window.scrollY })
            );
          } catch (e) {
            /* storage blocked: normal navigation */
          }
          return;
        }
        event.preventDefault();
        checkSwatch(swatch);
        if (isCurrent) {
          view = viewFromVariant(currentVariant);
        } else {
          view = {
            own: false,
            name: swatch.dataset.name,
            key: swatch.dataset.key,
            price: Number(swatch.dataset.price),
            compare: Number(swatch.dataset.compare),
            available: false,
            stock: 'out',
            qty: 0,
            variantId: swatch.dataset.variantId,
            restock: swatch.dataset.restock || '',
          };
        }
        render();
      });

      swatch.addEventListener('keydown', (event) => {
        const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
        if (!step) return;
        event.preventDefault();
        swatches[(i + step + swatches.length) % swatches.length].focus();
      });

      /* Warm up the other finish's page so the switch feels instant. */
      if (swatch.dataset.current !== 'true') {
        const prefetch = () => {
          if (swatch.dataset.prefetched) return;
          swatch.dataset.prefetched = 'true';
          const link = document.createElement('link');
          link.rel = 'prefetch';
          link.href = swatch.href;
          document.head.appendChild(link);
        };
        swatch.addEventListener('pointerenter', prefetch, { once: true });
        swatch.addEventListener('focus', prefetch, { once: true });
        swatch.addEventListener('touchstart', prefetch, { once: true, passive: true });
      }
    });

    altRows.forEach((row) => {
      row.addEventListener('click', (event) => {
        if (row.hasAttribute('data-current') && currentSwatch) {
          event.preventDefault();
          currentSwatch.click();
          root.querySelector('[data-bs-buy]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        try {
          sessionStorage.setItem(
            SCROLL_KEY,
            JSON.stringify({ path: new URL(row.href, location.href).pathname, y: window.scrollY })
          );
        } catch (e) {
          /* ignore */
        }
      });
    });

    /* ---- Variant options (products with real variants) ---- */

    const selectedOptions = () =>
      optionGroups.map((group) => {
        const on = group.querySelector('[data-bs-option-btn][aria-checked="true"]');
        return on ? on.dataset.value : null;
      });

    const refreshOptionStates = () => {
      const chosen = selectedOptions();
      optionGroups.forEach((group, gi) => {
        group.querySelectorAll('[data-bs-option-btn]').forEach((btn) => {
          const test = chosen.slice();
          test[gi] = btn.dataset.value;
          const match = variants.find((v) => v.options.every((o, oi) => o === test[oi]));
          const soldout = !match || !match.available;
          btn.classList.toggle('is-soldout', soldout);
          let tag = btn.querySelector('.bs-pdp-opt__out');
          if (soldout && !tag) {
            tag = document.createElement('span');
            tag.className = 'bs-pdp-opt__out';
            tag.textContent = root.querySelector('.bs-pdp-sw__out')?.textContent || 'Udsolgt';
            btn.appendChild(tag);
          } else if (!soldout && tag) {
            tag.remove();
          }
        });
        const valueEl = group.querySelector('[data-bs-option-value]');
        if (valueEl) valueEl.textContent = chosen[gi] || '';
      });
    };

    optionGroups.forEach((group) => {
      group.querySelectorAll('[data-bs-option-btn]').forEach((btn) => {
        btn.addEventListener('click', () => {
          group.querySelectorAll('[data-bs-option-btn]').forEach((b) => b.setAttribute('aria-checked', b === btn ? 'true' : 'false'));
          const chosen = selectedOptions();
          const match = variants.find((v) => v.options.every((o, oi) => o === chosen[oi]));
          refreshOptionStates();
          if (!match) return;
          currentVariant = match;
          if (currentSwatch) checkSwatch(currentSwatch);
          view = viewFromVariant(match);
          render();
          if (match.media && gallery && gallery.bsShowMedia) gallery.bsShowMedia(match.media);
          const url = new URL(location.href);
          url.searchParams.set('variant', match.id);
          history.replaceState(history.state, '', url.toString());
          document.dispatchEvent(new CustomEvent('bs:variant-change', { detail: { variant: match } }));
        });
      });
    });

    /* ---- Add to cart ---- */

    if (el.cartForm) {
      el.cartForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (!view || !view.own || !view.available) return;
        if (el.cartError) el.cartError.textContent = '';
        setBusy(el.atc, true);
        if (el.barBtn) setBusy(el.barBtn, true);
        try {
          await addToCart([{ id: Number(el.variantInput.value), quantity: 1 }], el.cartForm);
        } catch (err) {
          if (el.cartError) el.cartError.textContent = err.message;
        } finally {
          setBusy(el.atc, false);
          if (el.atc) el.atc.disabled = !view.available;
          if (el.barBtn) setBusy(el.barBtn, false);
        }
      });
    }

    /* ---- Back in stock (Klaviyo) ---- */

    if (el.notifyForm) {
      el.notifyForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = el.notifyForm.querySelector('[type="submit"]');
        const email = el.notifyForm.querySelector('[name="email"]').value.trim();
        const variant = el.notifyForm.querySelector('[name="variant"]').value;
        const company = el.notifyForm.dataset.company;

        if (el.notifyError) el.notifyError.textContent = '';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          if (el.notifyError) el.notifyError.textContent = texts.notify_invalid || '';
          return;
        }

        setBusy(button, true);
        try {
          const response = await fetch(
            `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(company)}`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/vnd.api+json', revision: '2024-10-15' },
              body: JSON.stringify({
                data: {
                  type: 'back-in-stock-subscription',
                  attributes: {
                    profile: { data: { type: 'profile', attributes: { email } } },
                    channels: ['EMAIL'],
                  },
                  relationships: {
                    variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variant}` } },
                  },
                },
              }),
            }
          );
          if (!response.ok) throw new Error(String(response.status));
          el.notifyForm.hidden = true;
          if (el.notifyDone) el.notifyDone.hidden = false;
        } catch (err) {
          if (el.notifyError) el.notifyError.textContent = texts.notify_error || '';
        } finally {
          setBusy(button, false);
        }
      });
    }

    /* ---- Sticky bar (mobile) ---- */

    const mobile = window.matchMedia('(max-width: 999px)');
    let barTicking = false;

    function updateBar() {
      if (!el.bar) return;
      const anchor = root.dataset.mode === 'notify' ? el.notifyWrap : el.actions;
      let show = false;
      if (anchor && mobile.matches) {
        const rect = anchor.getBoundingClientRect();
        show = rect.bottom < 0;
      }
      el.bar.classList.toggle('is-visible', show);
      el.bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (el.barBtn) el.barBtn.tabIndex = show ? 0 : -1;
    }

    if (el.bar) {
      document.body.classList.add('bs-pdp-has-bar');
      const onScroll = () => {
        if (barTicking) return;
        barTicking = true;
        requestAnimationFrame(() => {
          barTicking = false;
          updateBar();
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });

      el.barBtn?.addEventListener('click', () => {
        if (root.dataset.mode === 'notify') {
          el.notify?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = el.notifyForm && !el.notifyForm.hidden && el.notifyForm.querySelector('[name="email"]');
          if (input) setTimeout(() => input.focus({ preventScroll: true }), 400);
          return;
        }
        if (el.cartForm) {
          if (typeof el.cartForm.requestSubmit === 'function') el.cartForm.requestSubmit();
          else el.cartForm.dispatchEvent(new Event('submit', { cancelable: true }));
        }
      });
    }

    view = viewFromVariant(currentVariant || { id: 0, price: 0, compare: 0, available: false, stock: 'out', qty: 0 });
    if (optionGroups.length) refreshOptionStates();
    render();
  }

  /* ---------- Byg sættet færdigt ---------- */

  function bindSet(section) {
    if (section.dataset.bsBound) return;
    section.dataset.bsBound = 'true';
    const data = readJSON(section.querySelector('[data-bs-set-data]'));
    const texts = data.texts || {};
    const lines = Array.from(section.querySelectorAll('[data-bs-set-line]'));
    const totalEl = section.querySelector('[data-bs-set-total]');
    const textEl = section.querySelector('[data-bs-set-text]');
    const form = section.querySelector('[data-bs-set-form]');
    const button = section.querySelector('[data-bs-set-btn]');
    const error = section.querySelector('[data-bs-set-error]');
    let selfVariant = data.selfVariant;
    let selfPrice = Number(data.selfPrice) || 0;

    const word = (n) => NUMBER_WORDS[n] || String(n);

    const update = () => {
      const on = lines.filter((l) => l.classList.contains('is-on'));
      const count = on.length + (selfVariant ? 1 : 0);
      const all = on.length === lines.length;
      const total = on.reduce((sum, l) => sum + Number(l.dataset.price || 0), selfVariant ? selfPrice : 0);
      if (totalEl) totalEl.textContent = formatKr(total);
      if (textEl) textEl.textContent = fill(texts.box_text, { antal: count, finish: data.finish || '' });
      let label;
      if (count === 0) label = texts.button_none;
      else if (count === 1) label = texts.button_one;
      else if (all) label = fill(texts.button_all, { antal: word(count) });
      else label = fill(texts.button_some, { antal: word(count) });
      if (button) {
        button.textContent = label || '';
        button.disabled = count === 0;
      }
    };

    lines.forEach((line) => {
      const toggle = line.querySelector('[data-bs-set-toggle]');
      toggle?.addEventListener('click', () => {
        const on = !line.classList.contains('is-on');
        line.classList.toggle('is-on', on);
        toggle.setAttribute('aria-checked', on ? 'true' : 'false');
        update();
      });
    });

    document.addEventListener('bs:variant-change', (event) => {
      const v = event.detail && event.detail.variant;
      if (!v) return;
      selfVariant = v.available ? v.id : null;
      selfPrice = v.available ? v.price : 0;
      update();
    });

    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const items = [];
      if (selfVariant) items.push({ id: Number(selfVariant), quantity: 1 });
      lines.filter((l) => l.classList.contains('is-on')).forEach((l) => items.push({ id: Number(l.dataset.variantId), quantity: 1 }));
      if (!items.length) return;
      if (error) error.textContent = '';
      setBusy(button, true);
      try {
        await addToCart(items, form);
      } catch (err) {
        if (error) error.textContent = err.message;
      } finally {
        setBusy(button, false);
        update();
      }
    });

    update();
  }

  /* ---------- Related products via Shopify recommendations ---------- */

  async function loadRecs(section) {
    if (section.dataset.bsBound) return;
    section.dataset.bsBound = 'true';
    try {
      const response = await fetch(section.dataset.bsRecs);
      if (!response.ok) return;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const grid = doc.querySelector('[data-bs-recs-grid]');
      const target = section.querySelector('[data-bs-recs-grid]');
      if (grid && target && grid.children.length) {
        target.innerHTML = grid.innerHTML;
        section.hidden = false;
      }
    } catch (e) {
      /* no recommendations: the section stays hidden */
    }
  }

  /* ---------- Keep the scroll position when switching finish ---------- */

  function restoreScroll() {
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || 'null');
      sessionStorage.removeItem(SCROLL_KEY);
    } catch (e) {
      return;
    }
    if (!saved || saved.path !== location.pathname || !saved.y) return;
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    const go = () => window.scrollTo(0, saved.y);
    go();
    requestAnimationFrame(go);
    window.addEventListener('load', go, { once: true });
  }

  function init(root) {
    root.querySelectorAll('[data-bs-gallery]').forEach(bindGallery);
    root.querySelectorAll('[data-bs-pdp]').forEach(bindProduct);
    root.querySelectorAll('[data-bs-set]').forEach(bindSet);
    root.querySelectorAll('[data-bs-recs]').forEach(loadRecs);
  }

  window.BastantPDP = { init, formatKr };

  const start = () => {
    restoreScroll();
    init(document);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
